import React from 'react';
import {Navigate, Route} from 'react-router-dom';
import {apiDocsPlugin, ApiExplorerPage} from '@backstage/plugin-api-docs';
import {
  CatalogEntityPage,
  CatalogIndexPage,
  catalogPlugin,
  // CatalogTable,
} from '@backstage/plugin-catalog';
import {
  // CatalogImportPage,
  catalogImportPlugin,
} from '@backstage/plugin-catalog-import';
import {
  CostInsightsLabelDataflowInstructionsPage,
  CostInsightsPage,
  CostInsightsProjectGrowthInstructionsPage,
} from '@backstage-community/plugin-cost-insights';
import {ScaffolderPage, scaffolderPlugin} from '@backstage/plugin-scaffolder';
import {orgPlugin} from '@backstage/plugin-org';
import {SearchPage} from '@backstage/plugin-search';
import {TechRadarPage} from '@backstage-community/plugin-tech-radar';
import {
  TechDocsIndexPage,
  techdocsPlugin,
  TechDocsReaderPage,
} from '@backstage/plugin-techdocs';
import {TechDocsAddons} from '@backstage/plugin-techdocs-react';
import {ReportIssue} from '@backstage/plugin-techdocs-module-addons-contrib';
import {UserSettingsPage} from '@backstage/plugin-user-settings';
import {apis, oidcAuthApiRef} from './apis';
import {entityPage} from './components/catalog/EntityPage';
import {searchPage} from './components/search/SearchPage';
import {Root} from './components/Root';

import {
  AlertDisplay,
  OAuthRequestDialog,
  SignInPage,
} from '@backstage/core-components';
import {createApp} from '@backstage/app-defaults';
import {AppRouter, FlatRoutes} from '@backstage/core-app-api';
import {CatalogGraphPage} from '@backstage/plugin-catalog-graph';
// import { RequirePermission } from '@backstage/plugin-permission-react';
// import { catalogEntityCreatePermission } from '@backstage/plugin-catalog-common/alpha';
import {googleAuthApiRef} from '@backstage/core-plugin-api';
import {ExplorePage} from '@backstage-community/plugin-explore';
import { AmplitudeAnalytics } from '@internal/backstage-plugin-amplitude-analytics';
import { ScaffolderFieldExtensions } from '@backstage/plugin-scaffolder-react';
import { EntityListProvider } from '@backstage/plugin-catalog-react';
import { AccountTypeFieldExtension } from './scaffolder/AccountTypeField';

const providers = process.env.NODE_ENV === 'production' ? [{
    id: 'ping-id-auth',
    title: 'Ping ID',
    message: 'Sign in with Ping ID SSO',
    apiRef: oidcAuthApiRef
  }] :
  [{
    id: 'google-auth-provider',
    title: 'Google',
    message: 'Sign in using Google',
    apiRef: googleAuthApiRef,
  }]

const app = createApp({
  apis,
  bindRoutes({bind}) {
    bind(catalogPlugin.externalRoutes, {
      createComponent: scaffolderPlugin.routes.root,
      viewTechDoc: techdocsPlugin.routes.docRoot,
      createFromTemplate: scaffolderPlugin.routes.selectedTemplate,
    });
    bind(apiDocsPlugin.externalRoutes, {
      registerApi: catalogImportPlugin.routes.importPage,
    });
    bind(scaffolderPlugin.externalRoutes, {
      registerComponent: catalogImportPlugin.routes.importPage,
      viewTechDoc: techdocsPlugin.routes.docRoot,
    });
    bind(orgPlugin.externalRoutes, {
      catalogIndex: catalogPlugin.routes.catalogIndex,
    });
  },
  components: {
    SignInPage: props => (
      <SignInPage
        {...props}
        auto
        providers={providers}
      />
    ),
  },
});

const routes = (
  <FlatRoutes>
    <Route path="/" element={<Navigate to="catalog"/>}/>
    <Route
      path="/catalog"
      element={
        <CatalogIndexPage
          initialKind="domain"
          // Table customization:
          // actions={[]}
          // columns={[
          //   CatalogTable.columns.createNameColumn(),
          //   CatalogTable.columns.createOwnerColumn(),
          //   // Create a description column with custom width
          //   {
          //     title: 'Description',
          //     width: '50%',
          //     field: 'entity.description',
          //     render: ({ entity }) => entity.metadata.description,
          //   },
          //   CatalogTable.columns.createSpecTypeColumn(),
          //   CatalogTable.columns.createSystemColumn(),
          // ]}
          // tableOptions={{
          //   columnsButton: true,
          //   columnResizable: true,
          //   defaultExpanded: true,
          //   draggable: true,
          //   hideFilterIcons: true,
          //   padding: 'default',
          //   showDetailPanelIcon: true,
          //   showTitle: true,
          //   searchFieldVariant: 'outlined',
          //   toolbar: true,
          //   toolbarButtonAlignment: 'right',
          // }}
        />
      }
    />
    <Route
      path="/catalog/:namespace/:kind/:name"
      element={<CatalogEntityPage/>}
    >
      {entityPage}
    </Route>
    <Route path="/cost-insights" element={<CostInsightsPage/>}/>
    <Route
      path="/cost-insights/investigating-growth"
      element={<CostInsightsProjectGrowthInstructionsPage/>}
    />
    <Route
      path="/cost-insights/labeling-jobs"
      element={<CostInsightsLabelDataflowInstructionsPage/>}
    />
    <Route path="/docs" element={<TechDocsIndexPage/>}/>
    <Route
      path="/docs/:namespace/:kind/:name/*"
      element={<TechDocsReaderPage/>}
    >
      <TechDocsAddons>
        <ReportIssue/>
      </TechDocsAddons>
    </Route>
      <Route path="/create" element={
        <EntityListProvider>
          <ScaffolderPage>
            <ScaffolderFieldExtensions>
              <AccountTypeFieldExtension />
            </ScaffolderFieldExtensions>
          </ScaffolderPage>
        </EntityListProvider>
      } />
    <Route
      path="/api-docs"
      element={
        <ApiExplorerPage
          // Table customization:
          // actions={[]}
          // columns={[
          //   CatalogTable.columns.createNameColumn(),
          //   // Create a description column with custom width
          //   {
          //     title: 'Description',
          //     width: '20%',
          //     field: 'entity.description',
          //     render: ({ entity }) => entity.metadata.description,
          //   },
          //   CatalogTable.columns.createSystemColumn(),
          //   CatalogTable.columns.createOwnerColumn(),
          // ]}
        />
      }
    />
    <Route
      path="/tech-radar"
      element={<TechRadarPage width={1500} height={800}/>}
    />
    {/* <Route
      path="/catalog-import"
      element={
        <RequirePermission permission={catalogEntityCreatePermission}>
          <CatalogImportPage />
        </RequirePermission>
      }
    /> */}
    <Route path="/search" element={<SearchPage/>}>
      {searchPage}
    </Route>
    <Route path="/settings" element={<UserSettingsPage/>}/>
    <Route path="/catalog-graph" element={<CatalogGraphPage/>}/>
    <Route path="/explore" element={<ExplorePage/>}/>
  </FlatRoutes>
  
);

export default app.createRoot(
  <>
    <AlertDisplay/>
    <OAuthRequestDialog/>
    <AmplitudeAnalytics />
    <AppRouter>
      <Root>{routes}</Root>
    </AppRouter>
  </>,
);
